import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../config/api';
import { FONT_PRESETS, FONT_SLOT_DEFAULTS, resolveFontFamily } from '../config/fonts';

const FONT_MAP = {
  'modern-grotesk': { heading: "'Cabinet Grotesk', sans-serif", body: "'IBM Plex Sans', sans-serif" },
  'classic-luxury': { heading: "'Bodoni Moda', serif", body: "'IBM Plex Sans', sans-serif" },
  'round-friendly': { heading: "'Outfit', sans-serif", body: "'Manrope', sans-serif" },
  'impact-street': { heading: "'Anton', sans-serif", body: "'Inter', sans-serif" },
};

function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '255, 59, 48';
}

function ensureGoogleFont(preset) {
  if (!preset?.google) return;
  const id = `gf-${preset.id}`;
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${preset.google}&display=swap`;
  document.head.appendChild(link);
}

function ensureCustomFontFace(font) {
  if (!font?.url || !font?.family) return;
  const id = `cf-${font.id}`;
  if (document.getElementById(id)) return;
  const style = document.createElement('style');
  style.id = id;
  const format = font.format === 'ttf' ? 'truetype' : font.format === 'otf' ? 'opentype' : font.format;
  style.textContent = `
    @font-face {
      font-family: '${font.family.replace(/'/g, "\\'")}';
      src: url('${font.url}') format('${format}');
      font-display: swap;
    }
  `;
  document.head.appendChild(style);
}

export function ThemeInjector({ settings }) {
  const [customFonts, setCustomFonts] = useState([]);

  // Load custom fonts once
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/fonts`).then(({ data }) => {
      if (cancelled) return;
      setCustomFonts(data || []);
      (data || []).forEach(ensureCustomFontFace);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Apply colors + radius + legacy font preset
  useEffect(() => {
    const c = settings?.customize;
    if (!c) return;

    const root = document.documentElement;
    if (c.primary) {
      root.style.setProperty('--primary', c.primary);
      root.style.setProperty('--primary-rgb', hexToRgb(c.primary));
    }
    if (c.accent) {
      root.style.setProperty('--accent', c.accent);
      root.style.setProperty('--accent-rgb', hexToRgb(c.accent));
    }
    if (c.radius !== undefined) {
      root.style.setProperty('--radius', `${c.radius}px`);
    }
    // Legacy font pair preset
    if (c.font && FONT_MAP[c.font]) {
      root.style.setProperty('--font-heading', FONT_MAP[c.font].heading);
      root.style.setProperty('--font-body', FONT_MAP[c.font].body);
    }
    if (c.marquee_color) root.style.setProperty('--marquee-color', c.marquee_color);
    if (c.heartbeat_color) {
      root.style.setProperty('--heartbeat-color', c.heartbeat_color);
      root.style.setProperty('--heartbeat-rgb', hexToRgb(c.heartbeat_color));
    }
    if (c.header_bg) root.style.setProperty('--header-bg', c.header_bg);
    if (c.header_text) root.style.setProperty('--header-text', c.header_text);
    if (c.nav_color) root.style.setProperty('--nav-color', c.nav_color);
    if (c.ember_color_1) root.style.setProperty('--ember-color-1', c.ember_color_1);
    if (c.ember_color_2) root.style.setProperty('--ember-color-2', c.ember_color_2);
    if (c.ember_color_3) root.style.setProperty('--ember-color-3', c.ember_color_3);
  }, [settings?.customize]);

  // Font slots — applied only when explicitly configured (so legacy setups keep working)
  useEffect(() => {
    const slots = settings?.customize?.font_slots;
    const styles = settings?.customize?.font_slot_styles || {};
    if (!slots) return;
    const root = document.documentElement;

    const apply = (slotKey, famVar, wVar, lsVar, ttVar, smVar) => {
      const val = slots[slotKey] || FONT_SLOT_DEFAULTS[slotKey];
      const preset = FONT_PRESETS.find(f => f.id === val);
      if (preset) ensureGoogleFont(preset);
      const family = resolveFontFamily(val, customFonts);
      if (family) root.style.setProperty(famVar, family);

      const s = styles[slotKey] || {};
      root.style.setProperty(wVar, String(s.weight || ''));
      root.style.setProperty(lsVar, s.letter_spacing || 'normal');
      root.style.setProperty(ttVar, s.transform || 'none');
      root.style.setProperty(smVar, String(s.size_multiplier || 1));
    };

    apply('heading', '--font-heading', '--font-heading-weight', '--font-heading-spacing', '--font-heading-transform', '--font-heading-scale');
    apply('body',    '--font-body',    '--font-body-weight',    '--font-body-spacing',    '--font-body-transform',    '--font-body-scale');
    apply('display', '--font-display', '--font-display-weight', '--font-display-spacing', '--font-display-transform', '--font-display-scale');
    apply('mono',    '--font-mono',    '--font-mono-weight',    '--font-mono-spacing',    '--font-mono-transform',    '--font-mono-scale');

    if (slots.body || FONT_SLOT_DEFAULTS.body) {
      document.body.style.fontFamily = resolveFontFamily(slots.body || FONT_SLOT_DEFAULTS.body, customFonts, 'sans-serif');
    }
  }, [settings?.customize?.font_slots, settings?.customize?.font_slot_styles, customFonts]);

  // Button styles, borders, selection/scrollbar
  useEffect(() => {
    const c = settings?.customize || {};
    const btn = c.button_style || {};
    const border = c.border_style || {};
    const interaction = c.interaction || {};

    let style = document.getElementById('theme-injector-dynamic');
    if (!style) {
      style = document.createElement('style');
      style.id = 'theme-injector-dynamic';
      document.head.appendChild(style);
    }

    const shape = btn.shape || 'square';
    const shapeRadius = shape === 'pill' ? '9999px' : shape === 'rounded' ? '12px' : shape === 'tab' ? '12px 12px 0 0' : shape === 'blob' ? '60% 40% 50% 50% / 50% 60% 40% 50%' : '0px';
    const clipPaths = {
      slashed: 'polygon(8% 0, 100% 0, 92% 100%, 0 100%)',
      'cut-corner': 'polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 0 100%)',
      notched: 'polygon(0 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 10px 100%, 0 calc(100% - 10px))',
      chevron: 'polygon(0 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 0 100%, 14px 50%)',
    };
    const shapeClip = clipPaths[shape] || 'none';
    const shadow = Math.max(0, Math.min(100, btn.shadow ?? 0));
    const hover = btn.hover || 'fill';
    const uppercase = btn.uppercase !== false;

    const selectionColor = interaction.selection_color || c.primary || '#FF3B30';
    const selectionText = interaction.selection_text || '#FFFFFF';
    const scrollColor = interaction.scroll_color || c.primary || '#FF3B30';
    const scrollWidth = Math.max(0, Math.min(24, interaction.scroll_width ?? 10));

    const borderColor = border.color || '#262626';
    const borderThickness = Math.max(0, Math.min(6, border.thickness ?? 1));
    const borderStyle = border.style || 'solid';
    const cardRadius = Math.max(0, Math.min(48, border.card_radius ?? 0));

    const hoverRules = {
      fill: '',
      outline: `.btn-primary:hover, .btn-success:hover { background-color: transparent !important; box-shadow: inset 0 0 0 2px currentColor !important; }`,
      glow: `.btn-primary:hover, .btn-success:hover { box-shadow: 0 0 22px 2px rgba(var(--primary-rgb, 255, 59, 48), 0.55) !important; transform: translateY(-1px) !important; }`,
      shine: `.btn-primary, .btn-success { background-image: linear-gradient(120deg, transparent 0%, transparent 40%, rgba(255,255,255,0.25) 50%, transparent 60%, transparent 100%); background-size: 220% 100%; background-position: 100% 0; transition: background-position .6s ease, transform .15s ease !important; } .btn-primary:hover, .btn-success:hover { background-position: 0% 0 !important; }`,
    };
    const shadowCss = shadow > 0
      ? `.btn-primary, .btn-success { box-shadow: 0 ${Math.round(shadow / 10)}px ${Math.round(shadow / 3)}px rgba(0,0,0,${(shadow / 180).toFixed(3)}) !important; }`
      : '';

    // Special "neon" border: glowing outline using the accent/brand color
    const isNeon = borderStyle === 'neon';
    const effectiveStyle = isNeon ? 'solid' : borderStyle;
    const neonGlow = isNeon ? `box-shadow: 0 0 10px ${borderColor}, 0 0 2px ${borderColor};` : '';

    style.textContent = `
      :root {
        --btn-radius: ${shapeRadius};
        --site-border-color: ${borderColor};
        --site-border-style: ${effectiveStyle};
        --site-border-thickness: ${borderThickness}px;
        --card-radius: ${cardRadius}px;
      }
      .btn-primary, .btn-success { border-radius: ${shapeRadius} !important; text-transform: ${uppercase ? 'uppercase' : 'none'} !important; clip-path: ${shapeClip} !important; }
      ${shadowCss}
      ${hoverRules[hover] || ''}
      ::selection { background: ${selectionColor}; color: ${selectionText}; }
      ::-moz-selection { background: ${selectionColor}; color: ${selectionText}; }
      ${scrollWidth > 0 ? `::-webkit-scrollbar { width: ${scrollWidth}px; height: ${scrollWidth}px; } ::-webkit-scrollbar-track { background: #0A0A0A; } ::-webkit-scrollbar-thumb { background: ${scrollColor}; border-radius: ${shapeRadius === '0px' ? '0' : '4px'}; } ::-webkit-scrollbar-thumb:hover { background: ${scrollColor}cc; }` : ''}

      /* Global border color & style on the site's existing border utility classes */
      ${borderThickness > 0 ? `
        .border.border-\\[\\#262626\\], .border-\\[\\#262626\\] {
          border-color: ${borderColor} !important;
          border-style: ${effectiveStyle} !important;
          border-width: ${borderThickness}px !important;
          ${neonGlow}
        }
        .border.border-\\[\\#1a1a1a\\], .border-\\[\\#1a1a1a\\] {
          border-color: ${borderColor} !important;
          border-style: ${effectiveStyle} !important;
          ${neonGlow}
        }
      ` : ''}

      /* Card corner radius — applies to cards using the bordered container pattern */
      .card-radius, .bg-\\[\\#141414\\].border { border-radius: ${cardRadius}px; }

      /* Font slot advanced styles */
      .font-cabinet, h1, h2, h3, h4 { font-weight: var(--font-heading-weight, 900); letter-spacing: var(--font-heading-spacing, normal); text-transform: var(--font-heading-transform, none); }
      body, p { letter-spacing: var(--font-body-spacing, normal); text-transform: var(--font-body-transform, none); font-weight: var(--font-body-weight, 400); }
      .font-mono, code, kbd, pre, samp { letter-spacing: var(--font-mono-spacing, normal); text-transform: var(--font-mono-transform, none); font-weight: var(--font-mono-weight, 400); }
    `;
  }, [settings?.customize]);

  // Inject additional root/EKG/World CSS vars
  useEffect(() => {
    if (!settings) return;
    const root = document.documentElement;
    if (settings.ekg_speed) root.style.setProperty('--ekg-speed', String(settings.ekg_speed));
    if (settings.ekg_color) {
      root.style.setProperty('--heartbeat-color', settings.ekg_color);
      root.style.setProperty('--heartbeat-rgb', hexToRgb(settings.ekg_color));
    }
    if (settings.world_color) root.style.setProperty('--world-color', settings.world_color);
    if (settings.ember_color_1) root.style.setProperty('--ember-color-1', settings.ember_color_1);
    if (settings.ember_color_2) root.style.setProperty('--ember-color-2', settings.ember_color_2);
    if (settings.ember_color_3) root.style.setProperty('--ember-color-3', settings.ember_color_3);
    if (settings.customize?.btn_bg) root.style.setProperty('--btn-bg', settings.customize.btn_bg);
    if (settings.customize?.btn_text) root.style.setProperty('--btn-text', settings.customize.btn_text);
  }, [settings]);

  return null;
}

export { FONT_MAP };
